// In-page viewer for one brand/category/comparison report, opened by id
// from a Catalog row's "View report" icon. Same chrome and body as the
// View button in ReportHistorySection — that one already holds the row,
// this one fetches it first (GET /rmn/audit/reports/:id, the deep-link
// viewer's source) so a list row only needs the report id.

import React, { useEffect, useState } from 'react'
import { Box, CircularProgress, Dialog, DialogContent, IconButton, Typography } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import axiosInstant from '../../api'
import { rmnAuditReportEndpoint } from '../../api/endpoints'
import { ReportDialogBody, RmnAuditReportRow } from './auditReportShared'

export function ReportViewerDialog({
  reportId,
  onClose,
  rmnBrandId,
}: {
  reportId: number
  onClose: () => void
  // Internal users outside a brand workspace act as this root (rmnContext).
  rmnBrandId?: number | null
}) {
  const [row, setRow] = useState<RmnAuditReportRow | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    setRow(null)
    setError(null)
    axiosInstant
      .get(rmnBrandId ? `${rmnAuditReportEndpoint(reportId)}?rmn_brand_id=${rmnBrandId}` : rmnAuditReportEndpoint(reportId))
      .then((res) => {
        if (!cancelled) setRow(res.data as RmnAuditReportRow)
      })
      .catch(() => {
        if (!cancelled) setError("Couldn't load this report. Please try again.")
      })
    return () => {
      cancelled = true
    }
  }, [reportId, rmnBrandId])

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="md">
      <IconButton
        onClick={onClose}
        sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}
        aria-label="Close report"
      >
        <CloseIcon />
      </IconButton>
      <DialogContent sx={{ p: 0 }}>
        {!row && !error && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
            <CircularProgress size={26} />
          </Box>
        )}
        {error && (
          <Typography variant="body2" color="error" sx={{ textAlign: 'center', py: 10 }}>
            {error}
          </Typography>
        )}
        {row && !row.report && (
          <Typography variant="body2" sx={{ textAlign: 'center', py: 10, color: 'var(--text-2)' }}>
            This report isn&apos;t ready to view yet.
          </Typography>
        )}
        {row && row.report && <ReportDialogBody row={row} />}
      </DialogContent>
    </Dialog>
  )
}
